import type { Look, LookZone, PublicState } from "../protocol";
import { cardsIn } from "./field";
import { stackItemObjectId } from "./game-text";

export type Looks = Record<string, Look>;

export type PointedZone = { pid: string; zone: "library" | "graveyard" | "exile" };

export type Pointing = {
  hoverOid: number | null;
  handOrder: number[];
  zone: PointedZone | null;
  seat: string | null;
};

const NOTHING: Look = { kind: "LookingAtNothing", values: [] };
const ZONES: Record<PointedZone["zone"], LookZone> = {
  library: { kind: "Library", values: [] },
  graveyard: { kind: "Graveyard", values: [] },
  exile: { kind: "Exile", values: [] },
};

export function lookOf(st: PublicState, viewer: string, p: Pointing): Look {
  const oid = p.hoverOid;
  if (oid !== null) {
    const hand = handIndexOf(st, viewer, p.handOrder, oid);
    if (hand !== null) return { kind: "LookingAtHandCard", values: [hand] };
    if (isPublic(st, oid)) return { kind: "LookingAtObject", values: [oid] };
  }
  if (p.zone) return { kind: "LookingAtZone", values: [p.zone.pid, ZONES[p.zone.zone]] };
  if (p.seat) return { kind: "LookingAtPlayer", values: [p.seat] };
  return NOTHING;
}

function handIndexOf(st: PublicState, viewer: string, handOrder: number[], oid: number): number | null {
  const hand = st.players[viewer]?.hand;
  if (!hand || !hand[String(oid)]) return null;
  const order = new Map(handOrder.map((x, i) => [x, i]));
  const sorted = cardsIn(hand).map((c) => c.object_id)
    .sort((a, b) => (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER));
  return sorted.indexOf(oid);
}

function isPublic(st: PublicState, oid: number): boolean {
  const key = String(oid);
  if (st.stack.some((item) => stackItemObjectId(item) === oid)) return true;
  return Object.values(st.players).some((p) => key in p.battlefield || key in p.graveyard || key in p.exile || key in p.command_zone);
}

export function lookAnchors(st: PublicState, look: Look): string[] {
  switch (look.kind) {
    case "LookingAtObject": {
      const oid = look.values[0];
      const key = String(oid);
      const holder = Object.values(st.players).find((p) => key in p.graveyard || key in p.exile);
      if (!holder) return [`obj:${oid}`];
      return [`obj:${oid}`, `zone:${holder.id}:${key in holder.graveyard ? "graveyard" : "exile"}`];
    }
    case "LookingAtZone":
      return [`zone:${look.values[0]}:${look.values[1].kind.toLowerCase()}`];
    case "LookingAtPlayer":
      return [`seat:${look.values[0]}`];
    default:
      return [];
  }
}

export function lookedHandBack(looks: Looks, pid: string, handCount: number, shown: number): number | null {
  const look = looks[pid];
  if (look?.kind !== "LookingAtHandCard" || shown === 0) return null;
  const index = look.values[0];
  if (handCount <= shown) return Math.min(index, shown - 1);
  return Math.round((Math.min(index, handCount - 1) * (shown - 1)) / Math.max(1, handCount - 1));
}

const SEND_EVERY_MS = 90;
const NOTHING_AFTER_MS = 260;

export class LookSender {
  private sent = JSON.stringify(NOTHING);
  private wanted: Look = NOTHING;
  private wantedKey = this.sent;
  private timer: number | null = null;
  private lastSentAt = 0;

  constructor(private readonly send: (look: Look) => void) {}

  point(look: Look): void {
    const key = JSON.stringify(look);
    if (key === this.wantedKey) return;
    this.wanted = look;
    this.wantedKey = key;
    this.stop();
    if (key === this.sent) return;
    const wait = look.kind === "LookingAtNothing" ? NOTHING_AFTER_MS : Math.max(0, this.lastSentAt + SEND_EVERY_MS - performance.now());
    this.timer = window.setTimeout(() => this.flush(), wait);
  }

  private flush(): void {
    this.timer = null;
    this.sent = this.wantedKey;
    this.lastSentAt = performance.now();
    this.send(this.wanted);
  }

  stop(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
  }
}
